import { useCallback, useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import { useUser } from "@/hooks/useUser";
import { useToast } from "@/hooks/use-toast";
import { listingsApi } from "@/lib/listings-api";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { useDashboardInvalidator } from "@/hooks/dashboard-queries";

/**
 * Task #6370 — persistent notice shown on the host dashboard and My Garage
 * when the system auto-paused the host's listings because a booking request
 * expired without a response. Offers a one-click bulk "Reactivate listings"
 * action that only touches the auto-paused cars (manual pauses are left
 * alone — the server enforces that via the audit-log marker). Renders
 * nothing while loading or when the host has no auto-paused listings, so it
 * is safe to mount unconditionally.
 */
export function AutoPausedListingsBanner() {
  const { session } = useUser();
  const { t } = useAppTranslation();
  const { toast } = useToast();
  const dashboardInvalidator = useDashboardInvalidator();
  const token = session?.access_token;

  const [carIds, setCarIds] = useState<number[]>([]);
  const [reactivating, setReactivating] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    listingsApi
      .getAutoPausedStatus(token)
      .then((r) => { if (!cancelled) setCarIds(r.autoPaused ? r.carIds : []); })
      .catch(() => { /* silent — the banner is informational */ });
    return () => { cancelled = true; };
  }, [token]);

  const handleReactivate = useCallback(async () => {
    if (!token || reactivating) return;
    setReactivating(true);
    try {
      const result = await listingsApi.reactivateAutoPaused(token);
      const restoredCount = result.restored.length;
      const skippedCount = result.skipped.length;
      setCarIds(result.skipped.filter((s) => s.reason === "no_active_image").map((s) => s.carId));
      await Promise.all([
        dashboardInvalidator.invalidateListings(),
        dashboardInvalidator.invalidateOverview(),
      ]);
      if (restoredCount > 0) {
        toast({
          title: t("listings.autoPaused.reactivatedTitle", "Listings reactivated"),
          description:
            skippedCount > 0
              ? t(
                  "listings.autoPaused.reactivatedPartialDesc",
                  "Some listings could not be reactivated (for example, missing photos). Check My Garage for details.",
                )
              : t("listings.autoPaused.reactivatedDesc", "Your listings are live and visible in search again."),
        });
      } else {
        toast({
          title: t("listings.autoPaused.reactivateFailedTitle", "Couldn't reactivate listings"),
          description: t(
            "listings.autoPaused.reactivateFailedDesc",
            "Please try again, or reactivate each listing individually from My Garage.",
          ),
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: t("listings.autoPaused.reactivateFailedTitle", "Couldn't reactivate listings"),
        description: t(
          "listings.autoPaused.reactivateFailedDesc",
          "Please try again, or reactivate each listing individually from My Garage.",
        ),
        variant: "destructive",
      });
    } finally {
      setReactivating(false);
    }
  }, [token, reactivating, toast, t, dashboardInvalidator]);

  if (carIds.length === 0) return null;

  return (
    <div
      className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 sm:flex sm:items-center sm:justify-between sm:gap-4"
      role="alert"
      data-testid="auto-paused-banner"
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
        <div>
          <p className="font-semibold text-amber-900">
            {t("listings.autoPaused.title", "Your listings were paused")}
          </p>
          <p className="mt-1 text-sm text-amber-800">
            {t(
              "listings.autoPaused.description",
              "A booking request expired because we didn't hear back from you, so your listings were paused and removed from search. Reactivate them when you're ready to host again.",
            )}
          </p>
        </div>
      </div>
      <div className="mt-3 shrink-0 sm:mt-0">
        <button
          type="button"
          onClick={handleReactivate}
          disabled={reactivating}
          className="w-full rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-60 sm:w-auto"
          data-testid="auto-paused-reactivate"
        >
          {reactivating
            ? t("listings.autoPaused.reactivating", "Reactivating…")
            : t("listings.autoPaused.reactivate", "Reactivate listings")}
        </button>
      </div>
    </div>
  );
}
